import React from 'react';
import { flatten } from 'lodash';
import axios from 'axios';
import { useLocalStorage } from 'usehooks-ts';
import { scru128String } from 'scru128';
import { ErrorBoundary } from 'react-error-boundary';

import AISearch from './AISearch';
import AIThread, { AIThreadItem, AIThreadItemType } from './AIThread';

import './style.css';
import ErrorBoundaryFallback from './ErrorBoundaryFallback';
import { useFlipOIDC } from '../../components/FlipOIDC';

interface AIResponse {
  content: {
    parts: {
      text: string;
    }[];
  };
}

export default function AI() {
  const { token } = useFlipOIDC();

  const [value, setValue, clearValue] = useLocalStorage<AIThreadItem[]>('flip.demo.apps.ai.thread', []);
  const [currentLoading, setCurrentLoading] = React.useState<string | undefined>(undefined);

  const search = async (question: string) => {
    const { data } = await axios.post<AIResponse[]>('/api/v2/ai/ask', { question }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const updated: AIThreadItem[] = [{
      key: scru128String(),
      type: AIThreadItemType.Question,
      messages: [{ key: scru128String(), message: question }],
    }, {
      key: scru128String(),
      type: AIThreadItemType.Answer,
      messages: flatten(
        data.map((d) => (d.content.parts.map((p) => ({ key: scru128String(), message: p.text })))),
      ),
    }, ...value];

    setValue(updated);
    setCurrentLoading(undefined);
  };

  const handleQuestion = (question: string) => {
    setCurrentLoading(question);
    search(question);
  };

  const handleOnLetsGo = () => {
    handleQuestion('Hi there, can you tell me how you can help me?');
  };

  const handleLogout = async () => {
  };

  const handleClearHistory = () => {
    clearValue();
  };

  const handleError = () => {
    clearValue();
  };

  return (
    <ErrorBoundary FallbackComponent={ErrorBoundaryFallback} onError={handleError}>
      <div className="ai-layout">
        <div className="ai-search">
          <AISearch
            onChange={handleQuestion}
            onClearHistory={handleClearHistory}
            onLogout={handleLogout}
          />
        </div>
        <div className="ai-results">
          <AIThread value={value} loading={currentLoading} onLetsGo={handleOnLetsGo} />
        </div>
      </div>
    </ErrorBoundary>
  );
}
